import React from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * First-time guest offer:
 * "Continue with Google → unlock 250 bonus chips."
 *
 * Why? Anonymous chips live in localStorage and disappear if the user
 * clears their cache or switches device. Tying the profile to a Google
 * identity keeps progression durable AND seeds future Play Billing.
 */
export default function SignInOffer({ open, onClose, onSignIn, bonus = 250 }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[120] flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="signin-offer-overlay"
        >
          <div
            className="absolute inset-0"
            style={{ background: "rgba(5,5,7,0.78)", backdropFilter: "blur(14px)" }}
            onClick={onClose}
          />
          <motion.div
            className="relative glass rounded-3xl p-6 max-w-md w-full text-left"
            style={{ maxHeight: "92vh", overflowY: "auto" }}
            initial={{ y: 20, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 12, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            data-testid="signin-offer-modal"
            style={{ border: "1px solid rgba(255,215,0,0.35)" }}
          >
            <div className="flex items-center justify-between mb-3">
              <span
                className="label-eyebrow"
                style={{ color: "#FFD700", letterSpacing: 2 }}
              >
                LIMITED · ONE-TIME OFFER
              </span>
              <button
                onClick={onClose}
                className="text-white/55 hover:text-white text-xs underline"
                data-testid="signin-offer-dismiss"
              >
                Maybe later
              </button>
            </div>

            <h2 className="font-display font-black text-3xl leading-tight mb-2">
              Save your chips.<br />
              Pocket{" "}
              <span style={{ color: "#FFD700" }}>+{bonus} bonus</span>.
            </h2>
            <p className="text-white/70 text-sm mb-5">
              Sign in with Google and we'll drop{" "}
              <span className="font-bold text-white">{bonus} chips</span> straight
              into your stash. Your progress, levels, and streak stay locked to
              your account — even if you clear your cache or switch devices.
            </p>

            <div className="flex flex-col gap-2">
              <button
                onClick={onSignIn}
                data-testid="signin-offer-cta"
                className="rounded-full px-5 py-3 font-display font-bold flex items-center justify-center gap-3"
                style={{
                  background: "#fff",
                  color: "#0B0C10",
                  fontSize: 14,
                  boxShadow:
                    "0 0 24px rgba(255,255,255,0.28), 0 0 0 1px rgba(255,215,0,0.4) inset",
                }}
              >
                <svg width="16" height="16" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                  <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                </svg>
                Continue with Google · +{bonus} chips
              </button>
              <p className="text-[11px] text-white/45 text-center">
                One-time bonus · Awarded on your first sign-in
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
